// Help Modal - Voice Commands Reference
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

export function HelpButton() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={clsx(
          'w-10 h-10 rounded-full flex items-center justify-center',
          'bg-workshop-surface border border-workshop-border',
          'text-workshop-text-muted hover:text-white hover:border-workshop-accent',
          'transition-all duration-200 cursor-pointer hover:scale-105'
        )}
        title="Voice Commands Help"
      >
        <QuestionIcon className="w-5 h-5" />
      </button>

      <AnimatePresence>
        {isOpen && <HelpModal onClose={() => setIsOpen(false)} />}
      </AnimatePresence>
    </>
  );
}

function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[100] bg-black/60"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        drag
        dragMomentum={false}
        dragElastic={0.1}
        className="fixed z-[101] top-1/2 left-1/2 w-[90vw] max-w-md max-h-[85vh] overflow-hidden bg-workshop-bg border border-workshop-border rounded-2xl shadow-2xl cursor-default"
        style={{ x: '-50%', y: '-50%' }}
      >
        {/* Drag Handle */}
        <div className="flex justify-center py-2 cursor-grab active:cursor-grabbing bg-workshop-surface border-b border-workshop-border/50">
          <div className="w-12 h-1.5 rounded-full bg-workshop-border hover:bg-workshop-text-muted transition-colors" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-workshop-border bg-workshop-surface">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-workshop-accent/20 flex items-center justify-center">
              <MicIcon className="w-4 h-4 text-workshop-accent" />
            </div>
            <h2 className="text-lg font-semibold text-white">Voice Commands</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-workshop-text-muted hover:text-white hover:bg-workshop-border transition-all duration-150"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
          {/* Wake Word */}
          <Section title="Wake Word" accent>
            <div className="bg-workshop-accent/10 border border-workshop-accent/30 rounded-xl px-4 py-3 space-y-2">
              <p className="text-white text-center">
                Say <span className="font-bold text-workshop-accent">"Hey Ventura"</span> to activate
              </p>
              <p className="text-workshop-text-muted text-xs text-center">
                Stays listening for 15s — say "done" or "thanks" to stop
              </p>
            </div>
          </Section>

          {/* Search */}
          <Section title="Search">
            <CommandGrid commands={[
              { cmd: 'show me [part]', ex: '"show me the engine"' },
              { cmd: 'find [condition]', ex: '"find as received"' },
              { cmd: 'search [query]', ex: '"search steering wheel"' },
            ]} />
          </Section>

          {/* Select by Number */}
          <Section title="Select Images">
            <CommandGrid commands={[
              { cmd: 'image #1', ex: 'Open image 1' },
              { cmd: 'look at #3', ex: 'View image 3' },
              { cmd: 'select #5', ex: 'Select image 5' },
              { cmd: 'compare #1 and #2', ex: 'Compare two' },
            ]} />
          </Section>

          {/* Navigation */}
          <Section title="Navigation">
            <CommandGrid commands={[
              { cmd: 'next', ex: 'Next image' },
              { cmd: 'previous', ex: 'Previous image' },
              { cmd: 'go back', ex: 'Close view' },
              { cmd: 'home', ex: 'Main screen' },
            ]} />
          </Section>

          {/* Zoom & Compare */}
          <Section title="Zoom & More">
            <CommandGrid commands={[
              { cmd: 'zoom in', ex: 'Enlarge image' },
              { cmd: 'zoom out', ex: 'Shrink image' },
              { cmd: 'compare', ex: 'Before/after' },
              { cmd: 'similar', ex: 'Find similar' },
            ]} />
          </Section>

          {/* Keywords */}
          <Section title="Keywords">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-workshop-text-muted mb-2">Components</p>
                <div className="flex flex-wrap gap-1.5">
                  {['engine', 'body', 'interior', 'suspension', 'brakes', 'electrical', 'steering'].map((k) => (
                    <Chip key={k}>{k}</Chip>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-workshop-text-muted mb-2">Conditions</p>
                <div className="flex flex-wrap gap-1.5">
                  {['as received', 'restored', 'painted', 'completed'].map((k) => (
                    <Chip key={k}>{k}</Chip>
                  ))}
                </div>
              </div>
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-workshop-border bg-workshop-surface/50">
          <p className="text-workshop-text-muted text-xs text-center">
            Speak naturally — Ventura understands context
          </p>
        </div>
      </motion.div>
    </>
  );
}

function Section({ title, accent, children }: { title: string; accent?: boolean; children: React.ReactNode }) {
  return (
    <section>
      <h3 className={clsx(
        'text-sm font-medium mb-2',
        accent ? 'text-workshop-accent' : 'text-workshop-text-muted'
      )}>
        {title}
      </h3>
      {children}
    </section>
  );
}

function CommandGrid({ commands }: { commands: { cmd: string; ex: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {commands.map(({ cmd, ex }) => (
        <div key={cmd} className="bg-workshop-surface/50 rounded-lg px-3 py-2">
          <code className="text-white text-xs font-medium">{cmd}</code>
          <p className="text-workshop-text-muted text-xs mt-0.5">{ex}</p>
        </div>
      ))}
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-2 py-0.5 bg-workshop-border/50 rounded text-xs text-workshop-text-muted">
      {children}
    </span>
  );
}

function QuestionIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
